import React, { useState } from 'react';
import PageHeader from '../components/PageHeader';
import Reveal from '../components/Reveal';
import { Mail, Phone, MapPin, Check } from 'lucide-react';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: 'General enquiry', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Save locally so user sees confirmation without a backend
    const stored = JSON.parse(localStorage.getItem('andobree_messages') || '[]');
    stored.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem('andobree_messages', JSON.stringify(stored));
    setSent(true);
    setForm({ name: '', email: '', subject: 'General enquiry', message: '' });
    setTimeout(() => setSent(false), 4500);
  };

  return (
    <div>
      <PageHeader
        eyebrow="Contact Us"
        title="We&rsquo;re here, and we answer."
        intro="Our client care team answers every message within two working days. For urgent orders, call us directly."
      />

      <section className="py-20 px-6 lg:px-12">
        <div className="max-w-[1200px] mx-auto grid lg:grid-cols-[1fr_1.2fr] gap-16">
          {/* Info column */}
          <div className="space-y-10">
            <Reveal>
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                  <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500">Email</p>
                </div>
                <a href="mailto:andobreeofficial1@gmail.com" className="font-display text-2xl hover:underline underline-offset-4">
                  andobreeofficial1@gmail.com
                </a>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Phone className="w-4 h-4" strokeWidth={1.5} />
                  <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500">Phone</p>
                </div>
                <a href="tel:+36307465419" className="font-display text-2xl hover:underline underline-offset-4">
                  +36 30 746 5419
                </a>
                <p className="text-sm text-neutral-600 mt-2">Free from most EU numbers.</p>
              </div>
            </Reveal>
            <Reveal delay={200}>
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <MapPin className="w-4 h-4" strokeWidth={1.5} />
                  <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500">Location</p>
                </div>
                <p className="font-display text-2xl">Budapest, Hungary</p>
              </div>
            </Reveal>
          </div>

          {/* Form column */}
          <Reveal delay={120}>
            <form onSubmit={handleSubmit} className="bg-[#fafaf8] p-8 md:p-12">
              <h2 className="font-display text-2xl md:text-3xl font-medium mb-8">Send us a message</h2>

              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Name" required>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputClass}
                    placeholder="Your name"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={inputClass}
                    placeholder="your@email.com"
                  />
                </Field>
              </div>

              <Field label="Subject">
                <select
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className={inputClass}
                >
                  <option>General enquiry</option>
                  <option>Order support</option>
                  <option>Returns &amp; exchanges</option>
                  <option>Press</option>
                  <option>Wholesale</option>
                </select>
              </Field>

              <Field label="Message" required>
                <textarea
                  rows={6}
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className={`${inputClass} resize-none`}
                  placeholder="How can we help?"
                />
              </Field>

              <button
                type="submit"
                disabled={sent}
                className="w-full bg-neutral-900 text-white py-4 text-[11px] tracking-[0.22em] uppercase hover:bg-neutral-700 transition-colors mt-2 flex items-center justify-center gap-2"
              >
                {sent ? (
                  <>
                    <Check className="w-4 h-4" strokeWidth={1.5} /> Message Sent
                  </>
                ) : (
                  'Send Message'
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

const inputClass =
  'w-full border border-neutral-300 px-4 py-3.5 text-sm focus:outline-none focus:border-neutral-900 transition-colors bg-white placeholder:text-neutral-400';

const Field = ({ label, required, children }) => (
  <div className="mb-5">
    <label className="block text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-2">
      {label} {required && <span className="text-neutral-700">*</span>}
    </label>
    {children}
  </div>
);

export default Contact;
